import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CampaignIcon from "@mui/icons-material/Campaign";
import PersonIcon from "@mui/icons-material/Person";

import { StyledCard } from "../common/StyledCard";
import { NewsletterResponseDto } from "../../shared/dtos/newsletter/NewsletterResponseDto";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface NewsletterCardProps {
  newsletter: NewsletterResponseDto;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const NewsletterCard: React.FC<NewsletterCardProps> = ({
  newsletter,
  onView,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ maxWidth: "70%" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={newsletter.title}
            >
              {newsletter.title}
            </Typography>
            {newsletter.audienceScope && (
              <Chip
                label={String(newsletter.audienceScope).replace(/_/g, " ")}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                }}
              />
            )}
          </Box>
          <Stack direction="row">
            {onView && (
              <Tooltip title="Read Message">
                <IconButton size="small" color="info" onClick={onView}>
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit Content">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Content Preview */}
        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {newsletter.bodyContent}
          </Typography>
        </Box>

        {/* Footer Details */}
        <Stack spacing={0.8} sx={{ mt: "auto" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CalendarMonthIcon
              sx={{ fontSize: "1rem", color: "action.active" }}
            />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {formatDateForDisplay(newsletter.publicationDate)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CampaignIcon sx={{ fontSize: "1rem", color: "action.active" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {newsletter.academicYearName} - {newsletter.termName || "General"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <PersonIcon sx={{ fontSize: "1rem", color: "action.active" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Author: {newsletter.createdByName || "Staff"}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default NewsletterCard;
